import React, { useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import { Check, Play, RotateCcw } from 'lucide-react';
import videoCallPreview from '../../../../../../assets/images/graphics/video_call_preview.png';

export const FinalSubmitCard: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <Paper
        variant='outlined'
        sx={{
          width: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          textAlign: 'center',
          p: 5,
        }}
      >
        <Stack spacing={2} alignItems='center'>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'success.50',
              color: 'success.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Check size={28} strokeWidth={3} />
          </Box>
          <Box>
            <Typography variant='subtitle1' fontWeight={700} color='text.primary'>
              Final Submission Sent!
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Question completed
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant='outlined'
      sx={{
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        textAlign: 'left',
      }}
    >
      {/* ── Action Header ── */}
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 0.5 }}>
          <Chip
            label='FINAL STEP'
            size='small'
            color='primary'
            variant='outlined'
            sx={{ fontSize: 9, height: 20, fontWeight: 700 }}
          />
          <Typography variant='caption' color='text.secondary'>
            Review & Confirm
          </Typography>
        </Stack>
        <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
          Finalize your response and proceed to the next question.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          p: 2.5,
        }}
      >
        {/* Left – Playback Status Preview */}
        <Stack spacing={1.5}>
          <Box
            sx={{
              position: 'relative',
              bgcolor: 'common.black',
              borderRadius: 2,
              overflow: 'hidden',
              aspectRatio: '16/10',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component='img'
              src={videoCallPreview}
              alt='Professional Video Call'
              sx={{ height: '100%', width: 'auto', opacity: 0.6, objectFit: 'contain' }}
            />
            {/* Playback status indicator */}
            <Stack
              spacing={0.5}
              alignItems='center'
              sx={{
                position: 'absolute',
                inset: 0,
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.2)',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                <Play size={16} color='#ffffff' fill='#ffffff' />
              </Box>
              <Typography variant='caption' color='common.white' fontWeight={700} sx={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>
                Review Saved
              </Typography>
            </Stack>
          </Box>

          <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
              <Typography variant='caption' fontWeight={700} color='text.primary' sx={{ fontSize: 9, textTransform: 'uppercase' }}>
                Encoding Ready
              </Typography>
            </Stack>
            <Typography variant='caption' color='text.secondary' sx={{ fontSize: 8 }}>
              Video version 1.0 captured
            </Typography>
          </Paper>
        </Stack>

        {/* Right – Decision Panel */}
        <Stack spacing={2} justifyContent='center'>
          <Paper
            variant='outlined'
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'warning.50',
              borderColor: 'warning.light',
            }}
          >
            <Typography variant='caption' fontWeight={700} color='warning.dark' sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
              Change Mind?
            </Typography>
            <Typography variant='caption' color='warning.dark' sx={{ fontSize: 10, display: 'block', mb: 1.5 }}>
              Not happy? You can redo before hitting submit.
            </Typography>
            <Button
              variant='outlined'
              color='warning'
              size='small'
              fullWidth
              startIcon={<RotateCcw size={12} />}
              sx={{ fontSize: 10, py: 0.5 }}
            >
              Redo Recording
            </Button>
          </Paper>

          <Paper
            variant='outlined'
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'success.50',
              borderColor: 'success.light',
            }}
          >
            <Typography variant='caption' fontWeight={700} color='success.dark' sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
              Ready to go!
            </Typography>
            <Typography variant='caption' color='success.dark' sx={{ fontSize: 10, display: 'block', mb: 1.5 }}>
              Confirm your response is complete.
            </Typography>
            <Button
              onClick={() => setSubmitted(true)}
              variant='contained'
              color='success'
              size='small'
              fullWidth
              sx={{ fontSize: 11, fontWeight: 700, py: 0.75 }}
            >
              Submit Now
            </Button>
          </Paper>
        </Stack>
      </Box>
    </Paper>
  );
};
